import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { getAuth } from 'firebase/auth';
import { GetAnalysisById } from '../../../Client/ManagementServiceClient';
import StructuredAnalysisView from './StructuredAnalysisView';
import './AnalysisDetails.css';

const AnalysisDetails = () => {
  const { analysisId } = useParams();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      setError('You must be logged in to view analysis details');
      setLoading(false);
      return;
    }

    const fetchAnalysis = async () => {
      try {
        setLoading(true);
        const auth = getAuth();
        const idToken = await auth.currentUser.getIdToken();
        
        const result = await GetAnalysisById(idToken, analysisId);
        
        if (result.status === 200) {
          // Store the full response which contains analysis_results, analysis_timestamp, etc.
          const analysisData = result.myJson;
          setAnalysis(analysisData);
        } else if (result.status === 404) {
          setError('Analysis not found');
        } else {
          setError('Failed to load analysis');
        }
      } catch (err) {
        setError('Error loading analysis: ' + (err.message || 'Unknown error'));
        console.error('Error fetching analysis:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysis();
  }, [user, analysisId]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="analysis-details-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading analysis details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analysis-details-container">
        <div className="error-state">
          <p>{error}</p>
          <button className="btn-secondary" onClick={() => navigate('/AnalysisHistory')}>
            Back to History
          </button>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="analysis-details-container">
        <div className="error-state">
          <p>No analysis data available</p>
          <button className="btn-secondary" onClick={() => navigate('/AnalysisHistory')}>
            Back to History
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="analysis-details-container">
      <div className="details-header">
        <button className="btn-back" onClick={() => navigate('/AnalysisHistory')}>
          <ArrowLeft size={14} aria-hidden="true" /> Back to History
        </button>
        <h1>Video Analysis Results</h1>
        <div className="header-meta">
          <span>Analyzed: {formatDate(analysis.analysis_timestamp)}</span>
          {analysis.video_url && <span>Video: {analysis.video_url}</span>}
        </div>
      </div>

      <div className="transcript-section">
        {analysis.transcript && (
          <div className="transcript-box">
            <h3>Video Transcript</h3>
            <p className="transcript-text">{analysis.transcript}</p>
          </div>
        )}
      </div>

      <StructuredAnalysisView analysis={analysis} />

      <div className="details-footer">
        <button className="btn-secondary" onClick={() => navigate('/AnalysisHistory')}>
          Back to History
        </button>
        <button className="btn-primary" onClick={() => navigate('/VideoAnalysis')}>
          + Analyze Another Video
        </button>
      </div>
    </div>
  );
};

export default AnalysisDetails;
